import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getPatient,
  getGlobalPatient,
  getWorkPlan,
  listPatientPersonalFile,
  listHouseVisits,
  addEntityAsset,
  generateUploadURL,
  upload,
  updatePatient,
} from "../../api/server";
import { withNotifications } from "../notifications/notificationsSlice";
var unflatten = require("flat").unflatten;

const initialState = {
  error: null,
  activePatient: {},
  changedItems: [],
  changedValues: {},
  activePlan: {
    recurrent_events: [],
  },
  assets: [],
  patientRef: null,
  personalFile: {
    current: 1,
    pageSize: 1,
    filter: {
      freeText: null,
    },
    order: {
      field: "executed_at",
      order: "descend",
    },
    items: [],
  },
  houseVisits: {
    current: 1,
    pageSize: 10,
    filter: {
      freeText: null,
    },
    order: {
      field: "date",
      order: "descend",
    },
    items: [],
  },
};

export const loadRecord = createAsyncThunk(
  "patient/loadPatient",
  async (params, thunkAPI) => {
    return getPatient(params).then(({ error, data }) => {
      if (error) {
        // throw new NotificationError("Load Patient", error);
      }
      return data;
    });
  }
);

export const loadGlobalPatient = createAsyncThunk(
  "patient/loadGlobalPatient",
  async (params, thunkAPI) => {
    return getGlobalPatient(params.scopePrefix, params.filter).then(
      ({ error, data }) => {
        if (error) {
          // throw new NotificationError("Load employee", error);
        }
        return data;
      }
    );
  }
);

export const loadPlan = createAsyncThunk(
  "patient/loadPlan",
  async (params, thunkAPI) => {
    return getWorkPlan(params).then(({ error, data }) => {
      if (error) {
        // thunkAPI.dispatch(notifyError({ title: "Get Plan", message: error }));
      }
      return data;
    });
  }
);

export const fetchPersonalFile = createAsyncThunk(
  "patient/loadPersonalFile",
  async (listParams, thunkAPI) => {
    const { error, data } = await listPatientPersonalFile(listParams);
    if (error) {
      thunkAPI
        .dispatch
        // notifyError({ title: "Load patients", message: error })
        ();
    }
    return data;
  }
);

export const fetchHouseVisits = createAsyncThunk(
  "patient/loadHouseVisits",
  async (listParams, thunkAPI) => {
    const { error, data } = await listHouseVisits(listParams);
    if (error) {
      thunkAPI
        .dispatch
        // notifyError({ title: "Load patients", message: error })
        ();
    }
    return data;
  }
);

// export const addAsset = createAsyncThunk(
//   "patient/addAsset",
//   withNotifications(
//     async ({ branchId, id: resourceID, assetName, file }) => {
//       const {
//         data: { url },
//       } = await generateUploadURL(
//         "patients",
//         branchId,
//         resourceID,
//         assetName,
//         file
//       );
//       await upload(url, file);
//       return addEntityAsset("patients", branchId, resourceID, assetName, url);
//     },
//     "add_asset",
//     "add_asset_success",
//     "add_asset_error"
//   )
// );

export const saveChanges = createAsyncThunk(
  "patient/update",
  withNotifications(
    async (params, thunkAPI) => {
      return updatePatient(params);
    },
    "save_patient",
    "save_patient_success",
    "save_patient_error"
  )
);

export const commitChanges = createAsyncThunk(
  "patient/update",
  withNotifications(
    async (additionalParams, thunkAPI) => {
      const { patient } = thunkAPI.getState();
      const { activePatient } = patient;
      const { changedItems, changedValues } = patient;
      const toUpdate = changedItems.reduce((acc, key) => {
        acc[key] = changedValues[key] === undefined ? null : changedValues[key];
        return acc;
      }, {});
      const deepValues = unflatten(toUpdate);
      const params = {
        patient: { id: activePatient.id, ...deepValues },
        branchId: activePatient.branch,
      };
      return updatePatient(params);
    },
    "save_patient",
    "save_patient_success",
    "save_patient_error"
  )
);

export const addAsset = createAsyncThunk(
  "patient/addAsset",
  withNotifications(
    async ({ resource, branchId, id: resourceID, file, params }) => {
      const {
        data: { url },
      } = await generateUploadURL(
        resource,
        branchId,
        resourceID,
        params.name,
        file
      );
      await upload(url, file);
      return addEntityAsset(resource, branchId, resourceID, url, params);
    },
    "add_asset",
    "add_asset_success",
    "add_asset_error"
  )
);

const patientSlice = createSlice({
  name: "patientDetails",
  initialState: initialState,
  reducers: {
    setActivePatient(state, action) {
      const { record } = action.payload;
      state.activePatient = record;
    },
    setPatientRef(state, action) {
      const { record } = action.payload;
      state.patientRef = record;
    },
    setHouseVisits(state, action) {
      state.houseVisits = action.payload;
    },
    setChangedFormItems(state, action) {
      const { changed, values } = action.payload;
      state.changedItems = changed;
      state.changedValues = values;
    },
    reset(state, action) {
      state.error = null;
      state.activePatient = initialState.activePatient;
      state.changedItems = initialState.changedItems;
      state.changedValues = initialState.changedValues;

      state.activePlan = initialState.activePlan;
      state.assets = initialState.assets;
      state.personalFile = initialState.personalFile;
      state.houseVisits = initialState.houseVisits;
    },
  },
  extraReducers: {
    [loadRecord.fulfilled]: (state, action) => {
      state.changedItems = initialState.changedItems;
      state.changedValues = initialState.changedValues;
      state.activePatient = action.payload;
    },
    [loadPlan.fulfilled]: (state, action) => {
      state.activePlan = action.payload;
    },
    [loadPlan.rejected]: (state, action) => {
      state.error = action.error;
    },
    [fetchPersonalFile.fulfilled]: (state, action) => {
      state.personalFile = { ...state.personalFile, ...action.payload };
    },
    [fetchPersonalFile.rejected]: (state, action) => {
      state.error = action.error;
    },
    [fetchHouseVisits.fulfilled]: (state, action) => {
      state.houseVisits = { ...state.houseVisits, ...action.payload };
    },
    [fetchHouseVisits.rejected]: (state, action) => {
      state.error = action.error;
    },
  },
});

export const {
  setActivePatient,
  setPatientRef,
  setHouseVisits,
  setChangedFormItems,
  reset,
} = patientSlice.actions;

export default patientSlice.reducer;

export const loadPersonalFile = (params) => (dispatch, getState) => {
  const { patientId } = params;
  const newState = {
    ...getState().patient.personalFile,
  };
  const scopePrefix = getState().auth.scopePrefix;
  dispatch(fetchPersonalFile({ ...newState, patientId, scopePrefix }));
};

export const loadPatienthouseVisits = (params) => (dispatch, getState) => {
  const newState = {
    ...getState().patient.houseVisits,
    ...params,
  };
  const scopePrefix = getState().auth.scopePrefix;
  const patientId = getState().patient.activePatient.id;
  dispatch(setHouseVisits(newState));
  dispatch(fetchHouseVisits({ ...newState, scopePrefix, patientId }));
};
